import React, {
    useState, useEffect, useContext, useRef
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import classnames from 'classnames';
import { getLongFromShortCode } from '@homeaway/google-address-parser';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { Checkbox } from '@ferris/react/components/forms';
import withPageFlow from '../../../PageFlow/PageFlow';
import TranslationUtil from '../../../../utils/TranslationUtil';
import useDebouncedEffect from '../../../../utils/hooks/useDebouncedEffect';
import LocationActions from '../../../../actions/location';
import { keysToFields } from '../AutoComplete/AddressFormats';
import ProgressBarLYP from '../../../ProgressBarLYP';
import ExperimentUtil from '../../../../utils/ExperimentUtil';
import ErrorBanner from './ErrorBanner';
import MovableMap from '../../GoogleMap/MovableMap';
import GoogleApiLoader from '../../../../google/maps';
import { getConfigFromLocale } from '../AutoComplete/index';
import {
    sendCheckHasNoUnit, sendCheckHasUnit, sendFlowSummary, sendMissingUnit
} from '../../edap';
import {
    isClAddressImprovements,
    isGeocoderEnabled,
    isUnitCheckEnabled,
    isContentMessagingUpdatesEnabled,
    usesGeoFeed
} from '../../../../utils/ExperimentsUtil';
import Fields, { getFormInput } from './Fields';
import NextButton from './NextButton';
import {
    createInteractionEvent,
    createPageViewEvent,
    logLocationErrorEvent,
    logLocationInformationEvent
} from '../../../../utils/EventProviders/EgClickstreamUtil';
import { ERROR_INFORMATION, getErrorInfoClient } from '../../../../utils/ErrorUtil';
import JoinDetailsAction from '../../../../actions/joinDetails';
import { includePropertyName } from '../../../../utils/UnicodeUtil';
import log from '../../../../utils/ClientLogger';
import { getFieldOrder } from './util';
import { isMobileView } from '../../../../../server/utils/UIUtil';
import { isCountryInGoogleMapsExclusionList } from '../../../../utils/UserLocationUtil';
import { AAPMANUAL_LOCATION_PAGE } from '../../../PageFlow/PageList';

const PropertyNameFieldInput = (propertyNameField) => {
    const {
        setPropertyName,
        propertyNameLabel,
        propertyName,
        isShowingLatinPropertyName,
        setLatinPropertyName,
        latinPropertyName,
        brand,
        ignoreRTL
    } = propertyNameField;

    const handlePropertyNameChange = (event) => {
        setPropertyName(event.target.value);
    };
    const handleLatinPropertyNameChange = (event) => {
        setLatinPropertyName(event.target.value);
    };
    const classes = classnames('form-row', { 'form-row-ignore-rtl': ignoreRTL });

    return (
        <div>
            <div className={classes}>
                {
                    !isShowingLatinPropertyName ? (
                        <div className="property_details__form">
                            { getFormInput('latin-property-name', propertyNameLabel, latinPropertyName, handleLatinPropertyNameChange, '', brand) }
                        </div>
                    ) : (
                        <div className="property_details__form">
                            { getFormInput('property-name', propertyNameLabel, propertyName, handlePropertyNameChange, '', brand) }
                        </div>
                    )
                }
            </div>
            <div className="break" />
        </div>
    );
};

const GLANCE_MASKED = { glance_masked: 'true' };

const ManualLocation = (props) => {
    const {
        addressValidation,
        address,
        setAddressField,
        countryList,
        step,
        analytics,
        isGeoCoderEnabled,
        geocoderLatLng,
        geocoderValidation,
        locale,
        setLatLng,
        serverAddressValidation,
        clearAutoCompleteData,
        unitNumberValidation,
        unitCheckEnabled,
        brand,
        pathSource,
        isAddressV2,
        useGeoFeeds,
        userCountryCode,
        isAAP
    } = props;

    const { trigger } = useContext(TrackingContext);
    const [mapsApi, setMapsApi] = useState(null);
    const firstRender = useRef(false);
    const pageNameDetailed = `Manuallocation.${isGeoCoderEnabled ? 'LocationValidation' : 'Legacy'}.${pathSource}`;
    const validationErrors = useRef([]);

    // set any fields not in the format to the empty string to clear
    // out any excess data google may have jammed in there. This prevents
    // extraneous unicode from making it in.
    const cleanAddressFields = () => {
        const buildSet = (x) => { // Because IE 11 cannot use the proper set constructor.
            const s = new Set();
            x.forEach(i => s.add(i));
            return s;
        };
        const orderKeysSet = buildSet(
            (getFieldOrder(address.country).order.flat(Infinity)).map(key => keysToFields[key])
        );
        const addressKeysSet = Object.keys(address);
        const keysToClear = addressKeysSet.filter(x => !orderKeysSet.has(x) && address[x] !== '');
        keysToClear.forEach(x => setAddressField(x, ''));
    };
    useEffect(() => {
        const initialLoad = async () => {
            if (!isAddressV2) clearAutoCompleteData();
            if (!isMobileView()) window.scrollTo(0, 0);
            cleanAddressFields();
            setAddressField('address2', address.address2);
            trigger('pageLoad', { pageName: 'partner-qualifier: Manual Location' });
            trigger('pageview', createPageViewEvent('Manuallocation', pageNameDetailed));
            analytics.setAnalyticsPageName('JoinFormAddressFillDetails');
            analytics.viewTTIstop();
            trigger('pageLoadGtm', { pageName: 'PropertyAddressFillDetails', event: 'PropertyAddressFillDetailsLoad' });
            const config = getConfigFromLocale(locale);
            const googleApiLoader = await GoogleApiLoader.load(config);
            if (googleApiLoader) {
                setMapsApi(googleApiLoader);
            }
        };
        firstRender.current = true;
        initialLoad();
        return () => {
            analytics.viewTTIstart();
            trigger('pageUnload');
        };
    }, []);

    // <-- Prepare props of fields -->
    const handleAddressFieldChange = (e) => {
        setAddressField(e.target.name, e.target.value);
    };

    const [confirmNoUnitNumber, setConfirmNoUnitNumber] = useState(false);
    const { ignoreRTL } = getFieldOrder(address.country);

    const {
        title,
        titleFailure,
        latinRomanInfo,
        getTranslation,
        mapFootNote,
        propertyNameField,
        setPropertyName,
        setLatinPropertyName,
        features
    } = props;

    const fieldsProps = {
        unitNumberValidation,
        unitCheckEnabled,
        address,
        countryList,
        addressValidation,
        serverAddressValidation,
        handleAddressFieldChange,
        getTranslation,
        setConfirmNoUnitNumber,
        confirmNoUnitNumber,
        brand,
        locale: locale.canonical,
        useGeoFeeds,
        isConventionalLodgingFlow: features.isConventionalLodgingFlow,
        isAddressV2
    };
    const propertyNameFieldProps = {
        ...propertyNameField,
        ignoreRTL,
        brand,
        setPropertyName,
        setLatinPropertyName
    };
    // <-- Unit number validation -->
    const {
        value: uNValidationValue,
        success: unitNumberValidationSuccess,
        fail: unitNumberValidationFail
    } = unitNumberValidation;

    useEffect(() => {
        if (firstRender.current) {
            if (confirmNoUnitNumber) {
                sendCheckHasNoUnit(trigger);
            } else {
                sendCheckHasUnit(trigger);
            }
        }
    }, [confirmNoUnitNumber]);

    useEffect(() => {
        if (uNValidationValue.missingUnitNumber) {
            sendMissingUnit(trigger);
            trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION.MISSING_UNIT_NUMBER, pageNameDetailed, brand));
        } else if (!uNValidationValue.unitNumberValid) {
            sendMissingUnit(trigger);
            trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION.INVALID_UNIT_NUMBER, pageNameDetailed, brand));
        }
    }, [uNValidationValue]);
    const isAddress2Valid = (unitNumber) => {
        if (!unitNumberValidationSuccess && !unitNumberValidationFail) return true;
        if (confirmNoUnitNumber) return true;

        // user has changed the failed unit number
        // eslint-disable-next-line max-len
        if (!uNValidationValue.unitNumberValid && uNValidationValue.number === unitNumber) return false;
        if (uNValidationValue.missingUnitNumber && !unitNumber) return false;
        return true;
    };

    // <-- Form validation -->
    const isFormDataValid = () => {
        const keys = getFieldOrder(address.country).order.flat(Infinity);

        const allFieldsValid = keys.reduce(
            (acc, k) => (acc && addressValidation[keysToFields[k]].isValid),
            true
        );
        // eslint-disable-next-line max-len
        const allServerFieldsValid = keys.reduce((acc, k) => {
            if (keysToFields[k] === 'address2') return (acc && isAddress2Valid(address?.address2 || ''));
            return (acc && serverAddressValidation[keysToFields[k]].isValid);
        }, true) || !isGeoCoderEnabled;
        return { allFieldsValid, allServerFieldsValid };
    };

    // <-- Error gathering for moveon event -->
    const {
        error: geocoderLatLngError,
        success: geocoderLatLngSuccess,
        fail: geocoderLatLngFail,
        start: geocoderLatLngStart
    } = geocoderLatLng;

    const {
        lat,
        lng
    } = geocoderLatLng.value;

    const {
        errorList: geocoderValidationErrorList,
        value: isGeocoderValid,
        success: geocoderValidationSuccess,
        start: geocoderValidationStart,
        fail: geocoderValidationFail
    } = geocoderValidation;

    useEffect(() => {
        if (geocoderLatLngError) {
            validationErrors.current = Array.from(new Set([...validationErrors.current,
                geocoderLatLngError]));
        }
        if (geocoderValidationErrorList.length > 0) {
            validationErrors.current = Array.from(new Set([...validationErrors.current,
                ...geocoderValidationErrorList]));
        }
    }, [geocoderLatLngError, geocoderValidationErrorList]);

    // code block of errors being logged to egclickstream server address Validation
    {
        const {
            pinMap
        } = serverAddressValidation;

        useEffect(() => {
            if (pinMap.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[pinMap.errorCode], pageNameDetailed, brand));
            }
        }, [pinMap.errorCode]);

        const {
            address1
        } = serverAddressValidation;

        useEffect(() => {
            if (address1.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[address1.errorCode], pageNameDetailed, brand));
            }
        }, [address1.errorCode]);

        const {
            city
        } = serverAddressValidation;

        useEffect(() => {
            if (city.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[city.errorCode], pageNameDetailed, brand));
            }
        }, [city.errorCode]);

        const {
            postalCode
        } = serverAddressValidation;

        useEffect(() => {
            if (postalCode.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[postalCode.errorCode], pageNameDetailed, brand));
            }
        }, [postalCode.errorCode]);

        const {
            country
        } = serverAddressValidation;

        useEffect(() => {
            if (country.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[country.errorCode], pageNameDetailed, brand));
            }
        }, [country.errorCode]);

        const {
            stateProvince
        } = serverAddressValidation;

        useEffect(() => {
            if (stateProvince.errorCode) {
                trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[stateProvince.errorCode], pageNameDetailed, brand));
            }
        }, [stateProvince.errorCode]);

        const {
            generalState
        } = serverAddressValidation;

        useEffect(() => {
            if (!generalState.isValid && generalState.errorList.length > 0) {
                generalState.errorList.forEach((errorCode) => {
                    trigger('impression', logLocationErrorEvent(address, ERROR_INFORMATION[errorCode], pageNameDetailed, brand));
                });
            }
        }, [generalState.isValid]);
    }
    // code block for errors being logged to egclickstream clientside address Validation
    {
        const {
            address1: address1CsValidation,
            address2: address2CsValidation,
            city: cityCsValidation,
            stateProvince: stateCsValidation,
            postalCode: postalCsValidation,
            country: countryCsValidation
        } = addressValidation;

        useDebouncedEffect(() => {
            if (!address1CsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(address1CsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [address1CsValidation.errorCode], 500);

        useDebouncedEffect(() => {
            if (!address2CsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(address2CsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [address2CsValidation.errorCode], 500);

        useDebouncedEffect(() => {
            if (!cityCsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(cityCsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [cityCsValidation.errorCode], 500);

        useDebouncedEffect(() => {
            if (!stateCsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(stateCsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [stateCsValidation.errorCode], 500);

        useDebouncedEffect(() => {
            if (!postalCsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(postalCsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [postalCsValidation.errorCode], 500);

        useDebouncedEffect(() => {
            if (!countryCsValidation.isValid) {
                trigger('impression', logLocationErrorEvent(address, getErrorInfoClient(countryCsValidation.errorCode), pageNameDetailed, brand));
            }
        }, [countryCsValidation.errorCode], 500);
    }

    // <-- NextButton props -->
    const {
        buttonLabel,
        pageFlow,
        triggerGeoCoderValidation,
        triggerValidateUnitNumber
    } = props;
    const moveOn = () => {
        if (features.isConventionalLodgingFlow) {
            const gaData = {
                propertyAddress: address.address1,
                propertyCity: address.city,
                propertyZip: address.postalCode,
                propertyCountryRegion: address.country,
                propertyStateProvince: address.stateProvince,
                event: 'PropertyAddressSubmit',
                id: 'PropertyAddressSubmit'
            };
            trigger('gtmEvent', gaData);
        }
        trigger('interaction', logLocationInformationEvent(address, pageNameDetailed, brand));
        if (isGeoCoderEnabled) sendFlowSummary(trigger, true, validationErrors.current);
        log('info', 'location - manual location on next click', {
            address
        });
        pageFlow.next(isAAP && AAPMANUAL_LOCATION_PAGE);
    };
    const [canBypassValidation, setCanBypassValidation] = useState(isAddressV2);
    const [confirmsCorrectAddress, setConfirmsCorrectAddress] = useState(false);

    // <-- UI Props -->
    const {
        propertyNameTitle,
        propertyNameSubtitle,
        propertyAddressTitle
    } = props;

    const {
        allFieldsValid, allServerFieldsValid
    } = isFormDataValid();

    const nextButtonProps = {
        buttonLabel,
        triggerGeoCoderValidation,
        geocoderValidation,
        triggerValidateUnitNumber,
        unitNumberValidation,
        geocoderLatLng,
        isGeoCoderEnabled,
        unitCheckEnabled,
        allFieldsValid,
        allServerFieldsValid,
        trigger,
        confirmNoUnitNumber,
        serverAddressValidation,
        moveOn,
        canBypassValidation,
        confirmsCorrectAddress,
        isAddressV2
    };
    const titleToUse = (isAddressV2)
        && (geocoderLatLngError || !allFieldsValid || !allServerFieldsValid) ? titleFailure : title;
    const classes = classnames('ulx-form', 'ulx-animated', 'ulx-animated--movein');

    const handleConfirmCorrectAddress = (e) => {
        trigger('interaction', createInteractionEvent(`confirm_address.${e.target.checked ? 'selected' : 'deselected'}`, 'ManualLocation', 'ManualLocation.AddressConfirmation'));
        setConfirmsCorrectAddress(e.target.checked);
        log('info', 'Address Validation - Checkbox confirmed', {
            selected: e.target.checked,
            address
        });
    };

    const [showCheckbox, setShowCheckbox] = useState(false);
    useEffect(() => {
        setShowCheckbox(
            isAddressV2
        && allFieldsValid
        && allServerFieldsValid
        && (!geocoderLatLngStart && (geocoderLatLngFail || geocoderLatLngSuccess))
        && (!geocoderValidationStart && (geocoderValidationFail || geocoderValidationSuccess))
        && (
            geocoderLatLngError
            || serverAddressValidation.generalState.errorList.length >= 1
            || !isGeocoderValid
        )
        );
    }, [
        geocoderLatLngStart,
        geocoderValidationStart,
        geocoderLatLngFail,
        geocoderLatLngSuccess,
        geocoderValidationFail,
        geocoderValidationSuccess,
        geocoderLatLngError,
        isGeocoderValid,
        serverAddressValidation?.generalState?.errorList
    ]);

    const checkboxClasses = classnames('address_checkbox_confirm');

    return (
        <div className={classes} data-wdio="manual-location-container">
            <ProgressBarLYP step={step} />
            <ErrorBanner
                errors={serverAddressValidation.generalState.errorList}
                getTranslation={getTranslation}
                setCanBypassValidation={setCanBypassValidation}
                setConfirmsCorrectAddress={setConfirmsCorrectAddress}
                trigger={trigger}
                address={address}
                isCl={features.isConventionalLodgingFlow}
                display={
                    !isAddressV2
                    && isGeoCoderEnabled
                    && serverAddressValidation.generalState.errorList.length >= 1
                }
            />
            <h1>
                <b>{titleToUse}</b>
            </h1>
            {
                features.isConventionalLodgingFlow && (
                    <>
                        <h2>{propertyNameTitle}</h2>
                        <p className="small">{propertyNameSubtitle}</p>
                        <div className="ulx-form__form field__propertyName">
                            <PropertyNameFieldInput {...propertyNameFieldProps} />
                        </div>
                        <h2>{propertyAddressTitle}</h2>
                    </>
                )
            }
            <p className="small">{latinRomanInfo}</p>
            <div className="ulx-form__form" {...GLANCE_MASKED}>
                <Fields {...fieldsProps} />
                <MovableMap
                    display={
                        !isAddressV2
                        && isGeoCoderEnabled
                        && !isCountryInGoogleMapsExclusionList(userCountryCode)
                    }
                    trigger={trigger}
                    mapsApi={mapsApi}
                    lat={lat}
                    lng={lng}
                    onPositionChange={setLatLng}
                    text={mapFootNote}
                    error={serverAddressValidation.pinMap.error}
                    address={address}
                    pageName={pageNameDetailed}
                    brand={brand}
                />
                {showCheckbox && (
                    <Checkbox
                        onChange={handleConfirmCorrectAddress}
                        classes={checkboxClasses}
                        invalid
                        label={getTranslation('Vrbo.lyp.manualLocation.confirmAddress.checkbox')}
                        validationMessage={getTranslation('Cl.lyp.location.missingLocation.prompt')}
                    />
                )}
                <NextButton {...nextButtonProps} />
            </div>
        </div>
    );
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch, ownProps) => ({
    clearAutoCompleteData: () => {
        dispatch(LocationActions.setTypeaheadQuery(''));
        dispatch(LocationActions.setTypeaheadPredictions([]));
        dispatch(LocationActions.setLatLng({}));
    },
    setAddressField:
        (fieldName, value) => {
            if (value && fieldName !== 'address2') {
                dispatch(LocationActions.setPinLocation({}));
            }
            dispatch(LocationActions.setAddressField(fieldName, value,
                ownProps.features.isLatinInputEnabled));
        },
    triggerGeoCoderValidation: () => {
        dispatch(LocationActions.triggerGeoCoderValidation());
    },
    setLatLng: latLong => dispatch(LocationActions.setLatLng(latLong)),
    triggerValidateUnitNumber: () => {
        dispatch(LocationActions.triggerUnitNumberValidation());
    },
    setLatinPropertyName: (name) => {
        dispatch(JoinDetailsAction.setLatinPropertyName(name));
        dispatch(LocationActions.setPropertyName(name));
    },
    setPropertyName: (name) => {
        dispatch(JoinDetailsAction.setPropertyName(name));
        dispatch(LocationActions.setPropertyName(name));
    },
    setPinLocation: latLng => dispatch(LocationActions.setPinLocation(latLng))
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const {
        app, location, lead, joinDetails
    } = state;
    const {
        address,
        addressValidation,
        geocoderValidation,
        geocoderLatLng,
        unitNumberValidation,
        serverAddressValidation,
        pinLocation
    } = location;
    const {
        locale,
        isLoggedIn,
        loggedInUserType,
        hasAdvertiserForSite,
        features,
        brand,
        pathSource,
        userCountryCode
    } = app;
    const {
        propertyName, latinPropertyName
    } = joinDetails;
    const { missingInfo } = lead;
    const { hasPhoneNumber, hasPasswordSet } = missingInfo || {};
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const getTranslation = translation => translations.getTranslation(translation);
    const countriesByCode = translations.mapCountryTranslations();
    const countryList = Object.keys(countriesByCode).map(countryCode => ({
        text: countriesByCode[countryCode],
        value: countryCode.toUpperCase()
    }))
        // The phone list includes plaes that don't actually exist, so filter to countries with
        // actual valid ISO country codes
        .filter(item => !!getLongFromShortCode(item.value.toUpperCase()))
        .sort((a, b) => a.text.localeCompare(b.text));
    countryList.unshift({
        text: '--',
        value: ''
    });
    const step = features.isLocationFirst ? 1 : 2;

    const experimentUtil = new ExperimentUtil(app.experiments);
    const unitCheckEnabled = isUnitCheckEnabled(experimentUtil);
    const isGeoCoderEnabled = isGeocoderEnabled(experimentUtil);
    const contentMessagingUpdatesEnabled = isContentMessagingUpdatesEnabled(experimentUtil);
    const isAddressV2 = isClAddressImprovements(experimentUtil);

    const isShowingLatinPropertyName = includePropertyName(locale.canonical);

    const propertyNameLabel = translations.getTranslation('Cl.lyp.manualLocation.propertyName.label');

    const propertyNameField = {
        propertyNameLabel,
        propertyName,
        latinPropertyName,
        isShowingLatinPropertyName
    };
    const vrboTitle = getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.manualLocation.manualTitleTest' : 'Vrbo.lyp.manualLocation.manualTitle');
    // eslint-disable-next-line no-nested-ternary
    const title = (features.isConventionalLodgingFlow) ? getTranslation('CL.lyp.manualLocation.title.basics')
        : vrboTitle;
    const titleFailure = getTranslation('Cl.lyp.manualLocation.manualTitle.failure');

    const propertyNameTitle = getTranslation('Cl.lyp.manualLocation.propertyName.title');
    const propertyNameSubtitle = getTranslation('Cl.lyp.manualLocation.propertyName.subtitle');

    const propertyAddressTitle = getTranslation('Cl.lyp.manualLocation.propertyAddress.title');

    const useGeoFeeds = usesGeoFeed(experimentUtil);

    return {
        pinLocation,
        titleFailure,
        propertyNameTitle,
        propertyNameSubtitle,
        propertyAddressTitle,
        propertyNameField,
        brand,
        serverAddressValidation,
        mapFootNote: getTranslation('Vrbo.lyp.manualLocation.mapFootNote'),
        isGeoCoderEnabled,
        geocoderLatLng,
        geocoderValidation,
        unitNumberValidation,
        title,
        subTitle: getTranslation('Vrbo.lyp.manualLocation.subTitle'),
        latinRomanInfo: getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.manualLocation.latinRomanInfoTest' : 'Vrbo.lyp.manualLocation.latinRomanInfo'),
        buttonLabel: translations.getTranslation('Vrbo.cta.next'),
        locale,
        address,
        addressValidation,
        getTranslation,
        isLoggedIn,
        loggedInUserType,
        hasAdvertiserForSite,
        hasPhoneNumber,
        hasPasswordSet,
        countryList,
        step,
        features,
        unitCheckEnabled,
        pathSource,
        isAddressV2,
        useGeoFeeds,
        userCountryCode
    };
};

ManualLocation.propTypes = {
    setPinLocation: PropTypes.func.isRequired,
    titleFailure: PropTypes.string.isRequired,
    propertyNameTitle: PropTypes.string.isRequired,
    propertyNameSubtitle: PropTypes.string.isRequired,
    propertyAddressTitle: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    locale: PropTypes.shape({
        canonical: PropTypes.string,
        display: PropTypes.string
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func,
        location: PropTypes.shape({
            pathname: PropTypes.string
        }).isRequired
    }).isRequired,
    clearAutoCompleteData: PropTypes.func.isRequired,
    setLatLng: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    latinRomanInfo: PropTypes.string.isRequired,
    buttonLabel: PropTypes.string.isRequired,
    getTranslation: PropTypes.func.isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        })
    }).isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    setAddressField: PropTypes.func.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    countryList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string
    })).isRequired,
    step: PropTypes.number.isRequired,
    analytics: PropTypes.shape({
        viewTTIstart: PropTypes.func,
        viewTTIstop: PropTypes.func,
        setAnalyticsPageName: PropTypes.func
    }).isRequired,
    features: PropTypes.shape({
        isConventionalLodgingFlow: PropTypes.bool
    }).isRequired,
    isGeoCoderEnabled: PropTypes.bool.isRequired,
    triggerGeoCoderValidation: PropTypes.func.isRequired,
    geocoderLatLng: PropTypes.shape({
        value: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number
        }),
        error: PropTypes.string,
        start: PropTypes.bool.isRequired,
        success: PropTypes.bool.isRequired,
        fail: PropTypes.bool.isRequired
    }).isRequired,
    geocoderValidation: PropTypes.shape({
        value: PropTypes.bool,
        errorList: PropTypes.arrayOf(PropTypes.string),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    mapFootNote: PropTypes.string.isRequired,
    unitNumberValidation: PropTypes.shape({
        value: PropTypes.shape({
            missingUnitNumber: PropTypes.bool,
            number: PropTypes.string,
            unitNumberValid: PropTypes.bool
        }),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    triggerValidateUnitNumber: PropTypes.func.isRequired,
    unitCheckEnabled: PropTypes.bool.isRequired,
    serverAddressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        }),
        generalState: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            errorList: PropTypes.arrayOf(PropTypes.string)
        }),
        pinMap: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string,
            errorCode: PropTypes.string
        })
    }).isRequired,
    pathSource: PropTypes.string.isRequired,
    isAddressV2: PropTypes.bool.isRequired,
    setPropertyName: PropTypes.func.isRequired,
    setLatinPropertyName: PropTypes.func.isRequired,
    propertyNameField: PropTypes.shape({
        propertyNameLabel: PropTypes.element.isRequired,
        propertyName: PropTypes.string.isRequired,
        propertyNameValidationErrorMessage: PropTypes.string.isRequired,
        latinPropertyName: PropTypes.string.isRequired,
        latinPropertyNameValidationErrorMessage: PropTypes.string.isRequired,
        latinRomanTip: PropTypes.string.isRequired,
        isShowingLatinPropertyName: PropTypes.bool
    }).isRequired,
    pinLocation: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number
    }).isRequired,
    isTest: PropTypes.bool.isRequired,
    useGeoFeeds: PropTypes.bool.isRequired,
    userCountryCode: PropTypes.string.isRequired,
    isAAP: PropTypes.bool
};
ManualLocation.defaultProps = {
    isAAP: false
};
export { ManualLocation as ManualLocationComponent };
const ConnectedManualLocation = connect(mapStateToProps, mapDispatchToProps)(ManualLocation);
ConnectedManualLocation.displayName = 'ManualLocation';
export default hot(module)(withPageFlow(ConnectedManualLocation));
